import React, { ChangeEvent, FC, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notification, Dropdown } from 'antd';
import constants from '../../core/constants';
import { LatLng } from '../../types/types';

import './locationSearch.scss';

const DEBOUNCE_MS = 250;
const PLACES_API_URL = 'https://places.googleapis.com/v1/places';

interface Suggestion {
    placeId: string;
    main: string;
    secondary?: string;
}

const createSessionToken = () => {
    try {
        return crypto.randomUUID();
    } catch {
        return `${Date.now()}_${Math.random().toString(36).slice(2)}`;
    }
};

interface LocationSearchProps {
    active: boolean;
    onPlaceSelected: (latlng: LatLng) => void;
    onClose: () => void;
    width?: number;
}

const LocationSearch: FC<LocationSearchProps> = ({
    active,
    onPlaceSelected,
    onClose,
    width = 320,
}) => {
    const anchorRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
    const [popupWidth, setPopupWidth] = useState<number>(width);
    const sessionRef = useRef<string>(createSessionToken());
    const suggestAbortRef = useRef<AbortController | null>(null);
    const open = active && suggestions.length > 0;

    const debounced = useMemo(() => {
        let t: number | null = null;
        return (fn: () => void) => {
            if (t) {
                window.clearTimeout(t);
            }
            t = window.setTimeout(fn, DEBOUNCE_MS) as unknown as number;
        };
    }, []);
    const measure = useCallback(() => {
        const el = anchorRef.current;
        if (!el) {
            return;
        }
        const r = el.getBoundingClientRect();
        const vw = document.documentElement.clientWidth || window.innerWidth;
        const next = Math.max(160, Math.min(r.width, vw - 16));
        setPopupWidth(next);
    }, []);
    const fetchSuggestions = useCallback(async (q: string) => {
        if (!q.trim()) {
            setSuggestions([]);
            setHighlightedIndex(-1);
            return;
        }
        try {
            suggestAbortRef.current?.abort();
            const abortController = new AbortController();
            suggestAbortRef.current = abortController;
            const body = {
                input: q,
                sessionToken: sessionRef.current,
            };
            const response = await fetch(`${PLACES_API_URL}:autocomplete`, {
                method: 'POST',
                signal: abortController.signal,
                headers: {
                    'Content-Type': 'application/json',
                    'X-Goog-Api-Key': constants.GOOGLE_MAP_API_KEY,
                    'X-Goog-FieldMask': 'suggestions.placePrediction.placeId,suggestions.placePrediction.structuredFormat',
                },
                body: JSON.stringify(body),
            });
            if (!response.ok) {
                notification.error({
                    message: 'Error',
                    description: 'Failed to fetch suggestions, please try again',
                });
                return;
            }
            const data = await response.json();
            const next: Suggestion[] = (data?.suggestions ?? [])
                .filter((s: any) => s.placePrediction)
                .map((s: any) => {
                    const p = s.placePrediction;
                    return {
                        placeId: p.placeId,
                        main: p.structuredFormat?.mainText?.text ?? p.text?.text ?? '',
                        secondary: p.structuredFormat?.secondaryText?.text ?? '',
                    };
                });
            setSuggestions(next);
            setHighlightedIndex(next.length ? 0 : -1);
            if (next.length) {
                measure();
            }
        } catch (e: any) {
            if (e?.name === 'AbortError') {
                return;
            }
            notification.error({
                message: 'Error',
                description: 'Failed to fetch suggestions, please try again',
            });
            setSuggestions([]);
            setHighlightedIndex(-1);
        }
    }, [measure]);
    const fetchDetails = useCallback(async (placeId: string) => {
        try {
            const resp = await fetch(`${PLACES_API_URL}/${encodeURIComponent(placeId)}`, {
                method: 'GET',
                headers: {
                    'X-Goog-Api-Key': constants.GOOGLE_MAP_API_KEY,
                    'X-Goog-FieldMask': 'location',
                },
            });
            if (!resp.ok) {
                notification.error({
                    message: 'Error',
                    description: 'Failed to fetch place details, please try again',
                });
                return;
            }
            const place = await resp.json();
            const lat = place?.location?.latitude;
            const lng = place?.location?.longitude;
            if (Number.isFinite(lat) && Number.isFinite(lng)) {
                onPlaceSelected({ lat, lng });
                onClose();
                setSuggestions([]);
                return;
            }
            notification.error({
                message: 'Error',
                description: 'The selected place has no location',
            });
        } catch (err) {
            notification.error({
                message: 'Error',
                description: 'Failed to fetch place details, please try again',
            });
        }
    }, [onPlaceSelected, onClose]);

    const selectSuggestionByIndex = (i: number) => {
        const s = suggestions[i];
        if (!s) {
            return;
        }
        fetchDetails(s.placeId);
    };
    const onChange = (e: ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value;
        setQuery(v);
        debounced(() => fetchSuggestions(v));
    };
    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightedIndex((i) => {
                if (!suggestions.length) {
                    return -1;
                }
                return i < 0 
                    ? 0 
                    : Math.min(i + 1, suggestions.length - 1);
            });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (highlightedIndex >= 0) {
                selectSuggestionByIndex(highlightedIndex);
            }
        }
    };

    useEffect(() => {
        if (active) {
            setTimeout(() => inputRef.current?.focus(), 40);
            measure();
        } else {
            setQuery('');
            setSuggestions([]);
            setHighlightedIndex(-1);
            sessionRef.current = createSessionToken();
            suggestAbortRef.current?.abort();
            suggestAbortRef.current = null;
        }
    }, [active, measure]);
    useEffect(() => {
        if (!active) {
            return;
        }
        const fn = () => measure();
        window.addEventListener('resize', fn, { passive: true });
        const ro = new ResizeObserver(fn);
        if (anchorRef.current) {
            ro.observe(anchorRef.current);
        }
        return () => {
            window.removeEventListener('resize', fn);
            ro.disconnect();
        };
    }, [active, measure]);

    return (
        <Dropdown
            open={open}
            trigger={[]}
            placement="bottomLeft"
            destroyPopupOnHide
            getPopupContainer={() => document.body}
            overlayClassName="location-search-overlay"
            dropdownRender={() => (
                <div
                    onMouseDown={(e) => e.preventDefault()}
                    className='location-search_dropdown'
                    style={{ width: popupWidth }}
                >
                    {suggestions.map((s, i) => (
                        <div
                            key={s.placeId}
                            onClick={() => selectSuggestionByIndex(i)}
                            onMouseEnter={() => setHighlightedIndex(i)}
                            className='location-search_dropdown_option'
                            style={{
                                background: i === highlightedIndex 
                                    ? 'rgba(0,0,0,0.05)' 
                                    : 'transparent',
                            }}
                        >
                            <div className='location-search_dropdown_option_title'>{s.main}</div>
                            {s.secondary ? (
                                <div className='location-search_dropdown_option_address'>{s.secondary}</div> 
                            ) : null}
                        </div>
                    ))}
                </div>
            )}
        >
            <div
                ref={anchorRef}
                className='location-search'
                style={{
                    width: active ? width : 0,
                    marginLeft: active ? 0 : -8,
                }}
            >
                <div className='location-search_input-container'>
                    <input
                        ref={inputRef}
                        value={query}
                        onChange={onChange}
                        onKeyDown={onKeyDown}
                        placeholder="Search Address / Postcode"
                        spellCheck={false}
                        className='location-search_input'
                        onFocus={measure}
                    />
                </div>
            </div>
        </Dropdown>
    );
};

export default LocationSearch;
